import type { FC, ReactNode } from 'react'
import type { SunDay } from '@/lib/sun'

import { useId } from 'react'

interface SunPathProps {
    day: SunDay | null
    altitude: number | null
}

const WIDTH = 240
const HEIGHT = 52
const PAD_TOP = 6
const PAD_BOTTOM = 6
const FLOOR = -18

/**
 * Today's sun-path arc: altitude across the local day, the horizon as a hairline, a square at the
 * sun's current position (under the line at night) and a blue tick for the current time.
 */
export const SunPath: FC<SunPathProps> = ({ day, altitude }): ReactNode => {
    const clipId = useId().replace(/:/g, '')

    if (!day) {
        return <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className='block h-auto w-full' aria-hidden='true' />
    }

    const top = Math.max(12, day.noonAltitude) + 6
    const toY = (value: number): number => PAD_TOP + ((top - Math.min(top, Math.max(FLOOR, value))) / (top - FLOOR)) * (HEIGHT - PAD_TOP - PAD_BOTTOM)
    const toX = (hour: number): number => (hour / 24) * WIDTH
    const horizon = toY(0)
    const line = day.path.map((point, index) => `${index === 0 ? 'M' : 'L'}${toX(point.hour).toFixed(1)} ${toY(point.altitude).toFixed(1)}`).join('')
    const nowX = toX(day.localHour)
    const nowY = toY(altitude ?? 0)

    return (
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className='block h-auto w-full overflow-visible' aria-hidden='true'>
            <defs>
                <clipPath id={`${clipId}-above`}>
                    <rect x={0} y={0} width={WIDTH} height={horizon} />
                </clipPath>

                <clipPath id={`${clipId}-below`}>
                    <rect x={0} y={horizon} width={WIDTH} height={HEIGHT - horizon} />
                </clipPath>
            </defs>

            {[0, 6, 12, 18, 24].map(hour => (
                <line key={hour} x1={toX(hour)} x2={toX(hour)} y1={horizon - 2} y2={horizon + 2} stroke='#3A3630' strokeWidth={1} vectorEffect='non-scaling-stroke' />
            ))}

            <line x1={0} x2={WIDTH} y1={horizon} y2={horizon} stroke='#3A3630' strokeWidth={1} vectorEffect='non-scaling-stroke' />

            <path d={line} fill='none' stroke='#ECE6D8' strokeOpacity={0.55} strokeWidth={1} vectorEffect='non-scaling-stroke' clipPath={`url(#${clipId}-above)`} />
            <path d={line} fill='none' stroke='#6F6A60' strokeWidth={1} strokeDasharray='2 3' vectorEffect='non-scaling-stroke' clipPath={`url(#${clipId}-below)`} />

            <line x1={nowX} x2={nowX} y1={horizon - 6} y2={horizon + 6} stroke='#86B6F0' strokeWidth={1} vectorEffect='non-scaling-stroke' />
            <rect x={nowX - 2.5} y={nowY - 2.5} width={5} height={5} fill='#ECE6D8' />
        </svg>
    )
}
